// Event Operations für MIDI (Pro Tools Reference Guide Kap. 45): Quantize mit Stärke und Swing,
// Velocity, Notenlänge, Transponieren. Arbeiten auf Noten (Beats relativ zum Clip) und geben neue Noten zurück.
import type { Note } from '@srs/shared';

export interface QuantizeOpts {
  /** Raster in Beats */
  grid: number;
  /** 0..100 %: wie weit die Noten zum Raster rücken */
  strength: number;
  /** 0..100 %: Swing, verschiebt jede zweite Rasterposition nach hinten (bis zur Triole bei 100 %) */
  swing: number;
  /** Notenenden mit quantisieren */
  ends: boolean;
  /** Bereich: nur Noten innerhalb dieses Abstands (in % des Rasters) werden bewegt; 100 = alle */
  window: number;
}
export const DEFAULT_QUANTIZE: QuantizeOpts = { grid: 0.25, strength: 100, swing: 0, ends: false, window: 100 };

/** Rasterposition mit Swing */
function target(t: number, o: QuantizeOpts) {
  const k = Math.round(t / o.grid);
  const base = k * o.grid;
  return k % 2 === 1 ? base + (o.swing / 100) * (o.grid / 3) : base;
}
export function quantizeNotes(notes: Note[], o: QuantizeOpts): Note[] {
  const s = Math.max(0, Math.min(100, o.strength)) / 100;
  return notes.map((n) => {
    const tq = target(n.t, o);
    if (Math.abs(tq - n.t) > (o.window / 100) * (o.grid / 2) + 1e-9) return n;
    const t = n.t + (tq - n.t) * s;
    let d = n.d;
    if (o.ends) { const endQ = Math.max(tq + o.grid, Math.round((n.t + n.d) / o.grid) * o.grid); d = Math.max(o.grid / 4, n.d + (endQ - tq - n.d) * s); }
    return { ...n, t: Math.max(0, Math.round(t * 10000) / 10000), d: Math.round(d * 10000) / 10000 };
  });
}

export type VelocityMode = 'set' | 'add' | 'scale' | 'humanize' | 'compress';
/** Velocity: setzen (0..127), addieren, skalieren (%), zufällig streuen (±), zur Mitte stauchen (%) */
export function changeVelocity(notes: Note[], mode: VelocityMode, value: number, rnd = Math.random): Note[] {
  return notes.map((n) => {
    const v = n.v * 127;
    let x = v;
    if (mode === 'set') x = value;
    else if (mode === 'add') x = v + value;
    else if (mode === 'scale') x = (v * value) / 100;
    else if (mode === 'humanize') x = v + (rnd() * 2 - 1) * value;
    else if (mode === 'compress') x = 80 + ((v - 80) * value) / 100;
    return { ...n, v: Math.max(1, Math.min(127, Math.round(x))) / 127 };
  });
}

export type DurationMode = 'set' | 'scale' | 'legato' | 'add';
/** Notenlänge: feste Länge (Beats), skalieren (%), Legato (bis zur nächsten Note), verlängern/verkürzen (Beats) */
export function changeDuration(notes: Note[], mode: DurationMode, value: number): Note[] {
  const starts = [...new Set(notes.map((n) => n.t))].sort((a, b) => a - b);
  return notes.map((n) => {
    let d = n.d;
    if (mode === 'set') d = value;
    else if (mode === 'scale') d = (n.d * value) / 100;
    else if (mode === 'add') d = n.d + value;
    else { const next = starts.find((s) => s > n.t + 1e-9); d = next !== undefined ? next - n.t : n.d; }
    return { ...n, d: Math.max(1 / 64, Math.round(d * 10000) / 10000) };
  });
}

export function transpose(notes: Note[], semis: number): Note[] {
  return notes.map((n) => ({ ...n, m: Math.max(0, Math.min(127, n.m + semis)) }));
}

/** doppelte Noten (gleiche Höhe, gleicher Anfang) entfernen */
export function removeDuplicates(notes: Note[]): Note[] {
  const seen = new Set<string>();
  return notes.filter((n) => { const k = `${n.m}|${Math.round(n.t * 1000)}`; if (seen.has(k)) return false; seen.add(k); return true; });
}
